/** Platform-independent offline command ownership, replay, and ledger rules. */

import { SessionChangedError } from '../session-scope'

/** Every write that may be captured while offline. Nothing else is eligible. */
export type OfflineMutationKind =
  | 'task.create'
  | 'task.status'
  | 'time-entry.create'
  | 'thread-comment.create'
  | 'thread.create'

/**
 * Mutation keys registered through `setMutationDefaults`. The leading
 * `'offline'` segment is load-bearing: it is what `isOfflineMutationKey`
 * matches on, and therefore what decides whether a paused mutation is written
 * to disk at all.
 */
export const OFFLINE_MUTATION_KEYS: Record<
  OfflineMutationKind,
  readonly string[]
> = {
  'task.create': ['offline', 'task', 'create'],
  'task.status': ['offline', 'task', 'status'],
  'time-entry.create': ['offline', 'time-entry', 'create'],
  'thread-comment.create': ['offline', 'thread-comment', 'create'],
  'thread.create': ['offline', 'thread', 'create'],
}

export type ReplaySafety =
  | 'server-deduped'
  | 'naturally-idempotent'
  | 'at-most-once'

export const REPLAY_SAFETY: Record<OfflineMutationKind, ReplaySafety> = {
  // Historical task captures may predate server receipts or a compatible client.
  // Keep the conservative policy for those already-sent envelopes.
  'task.create': 'at-most-once',
  // Status updates also stamp completion time and cycle duration. Repeating
  // an acknowledged-but-lost request can change reporting data, even if the
  // target status is unchanged. There is no command receipt for this route.
  'task.status': 'at-most-once',
  // New captures carry an operationId whose server receipt deduplicates a
  // resend and can be read back after a lost response. Historical envelopes
  // may predate receipts; a duplicate is double-logged hours flowing into
  // timesheets and billing, so the queue still never replays blind.
  'time-entry.create': 'at-most-once',
  // thread_comment carries UNIQUE (threadId, authorId, clientMessageId) and the
  // repository turns the resulting P2002 into "return the existing row". This
  // is the pattern the rest of the queue is modelled on.
  'thread-comment.create': 'server-deduped',
  // Only versioned owned creation captures are admitted by this handler. Their
  // server receipt commits the discussion and optional first message together.
  'thread.create': 'server-deduped',
}

/**
 * Documentation-only, and deliberately so: it exists to make the next person
 * adding a mutation key decide which side of the line it falls on.
 *
 * A queued financial write that fires on reconnect against context that moved
 * hours ago is strictly worse than an error message the user can act on.
 */
export const OFFLINE_EXCLUSIONS = [
  'Financial: invoice create/send/void, payment record/refund, quote send/accept, credit purchases, subscription and billing changes, contract plan changes.',
  'Approvals: expense approval, timesheet approval, submit-for-payroll, payroll runs.',
  'Destructive: deletes, merges, archive/restore — a replayed delete cannot be undone.',
  'Privileged: permission, role, membership and organization-settings changes.',
  'Binary bodies: file and attachment uploads do not survive JSON serialization.',
  'Writes whose target was itself created offline — the queue has no dependency ordering, so commenting on an unsynced task is refused at the composer.',
] as const

/** How long a captured write stays replayable before it is treated as stale. */
export const OFFLINE_QUEUE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000

/** Replay attempts before a write stops retrying and asks for a decision. */
export const OFFLINE_QUEUE_MAX_ATTEMPTS = 5

/** Minimum retention after acknowledgement; also retain through capture expiry. */
export const OFFLINE_LEDGER_RETENTION_MS = 24 * 60 * 60 * 1000

/**
 * The variables of every eligible mutation. `idempotencyKey` is generated once
 * at the call site — NOT inside `mutationFn`, which re-runs on every replay —
 * and is what makes a replay recognizable as the same write.
 */
export interface OfflineMutationEnvelope<TPayload = unknown> {
  version: 2
  ownerId: string
  /** Capture provenance; dispatch uses a fresh session so work survives sign-in. */
  sessionGeneration: number
  idempotencyKey: string
  kind: OfflineMutationKind
  /** Requests carry the org as a header, so it has to travel in the payload. */
  organizationId: string
  enqueuedAt: number
  /** Shown verbatim in the pending-writes list. Keep it human. */
  label: string
  payload: TPayload
  /** Original task-row revision; never filled from a newer replay-time read. */
  recordRevision?: number
}

export type OfflineLedgerStatus =
  | 'queued'
  | 'inflight'
  | 'succeeded'
  | 'failed'
  | 'conflicted'
  | 'needs-review'
  | 'expired'

/**
 * The ledger stores the whole envelope, not just metadata. TanStack only
 * persists *paused* mutations, so a write that errored while online would
 * otherwise vanish on reload; keeping the envelope means the pending-writes UI
 * can re-dispatch it from a cold start.
 */
export interface OfflineLedgerEntry {
  envelope: OfflineMutationEnvelope
  status: OfflineLedgerStatus
  attempts: number
  updatedAt: number
  /** Same-session response; never persisted or reused after its retention limit. */
  result?: unknown
  /** A closed operation ID, without its draft or a reusable server response. */
  completionOnly?: true
  /** User-facing explanation for `failed` / `needs-review` / `expired`. */
  message?: string
}

export interface OfflineLedger {
  get(idempotencyKey: string, ownerId?: string): OfflineLedgerEntry | undefined
  list(): OfflineLedgerEntry[]
  upsert(entry: OfflineLedgerEntry): void
  patch(
    idempotencyKey: string,
    changes: Partial<Omit<OfflineLedgerEntry, 'envelope'>>,
    ownerId?: string,
  ): OfflineLedgerEntry | undefined
  remove(idempotencyKey: string, ownerId?: string): void
  subscribe(listener: () => void): () => void
  prune(now?: number): void
  hydrate(entries: OfflineLedgerEntry[]): void
}

/**
 * In-memory ledger with an optional write-through to the host's storage.
 * Hosts must await durable capture and dispatch markers before confirming
 * queued work or sending it. Background persistence alone is not a receipt.
 */
export function createOfflineLedger(
  persist?: (entries: OfflineLedgerEntry[]) => void,
): OfflineLedger {
  const entries = new Map<string, OfflineLedgerEntry>()
  const keys = new Map<string, Set<string>>()
  const listeners = new Set<() => void>()
  const keyFor = (envelope: OfflineMutationEnvelope) =>
    JSON.stringify([envelope.ownerId, envelope.idempotencyKey])
  const resolveKey = (key: string, ownerId?: string) => {
    if (ownerId !== undefined) return JSON.stringify([ownerId, key])
    const matches = keys.get(key)
    // Older callers may omit the owner only when the operation is unambiguous.
    if (matches && matches.size > 1) throw new OfflineWriteOwnerError()
    return matches?.values().next().value as string | undefined
  }
  const set = (entry: OfflineLedgerEntry) => {
    const key = keyFor(entry.envelope)
    const current = entries.get(key)
    if (current && !isSameLedgerIntent(current, entry))
      throw new OfflineWriteIntentConflictError()
    if (current?.status === 'succeeded' && entry.status !== 'succeeded')
      throw new OfflineWriteIntentConflictError()
    if (
      current?.status === 'conflicted' &&
      !['conflicted', 'succeeded'].includes(entry.status)
    )
      throw new OfflineWriteIntentConflictError()
    entries.set(
      key,
      Object.freeze({
        ...entry,
        envelope: current?.envelope ?? freezeOfflineEnvelope(entry.envelope),
        ...((current?.status === 'succeeded' && current.completionOnly) ||
        (entry.status === 'succeeded' && entry.completionOnly)
          ? completionMarker(current ?? entry)
          : {}),
      }),
    )
    let owners = keys.get(entry.envelope.idempotencyKey)
    if (!owners) {
      owners = new Set()
      keys.set(entry.envelope.idempotencyKey, owners)
    }
    owners.add(key)
  }
  const erase = (key: string) => {
    const entry = entries.get(key)
    if (!entry) return false
    entries.delete(key)
    const owners = keys.get(entry.envelope.idempotencyKey)
    owners?.delete(key)
    if (!owners?.size) keys.delete(entry.envelope.idempotencyKey)
    return true
  }

  const emit = () => {
    const snapshot = [...entries.values()].map(entry =>
      entry.status === 'succeeded'
        ? Object.freeze({ ...entry, ...completionMarker(entry) })
        : entry,
    )
    // Completion must survive independently of TanStack's paused snapshot.
    // Closed IDs suppress delivery; they never supply a result or validate a
    // replacement body. Neither drafts nor responses belong in these markers.
    persist?.(snapshot)
    listeners.forEach(listener => {
      try {
        listener()
      } catch {
        // A broken subscriber must not take the queue down with it.
      }
    })
  }

  const byQueuedAt = (a: OfflineLedgerEntry, b: OfflineLedgerEntry) =>
    a.envelope.enqueuedAt - b.envelope.enqueuedAt

  return {
    get: (key, ownerId) => {
      const resolved = resolveKey(key, ownerId)
      return resolved === undefined ? undefined : entries.get(resolved)
    },
    list: () => [...entries.values()].sort(byQueuedAt),
    upsert: entry => {
      set(entry)
      emit()
    },
    patch: (key, changes, ownerId) => {
      const resolved = resolveKey(key, ownerId)
      const existing =
        resolved === undefined ? undefined : entries.get(resolved)
      if (!existing) return undefined
      const next = {
        ...existing,
        ...changes,
        envelope: existing.envelope,
        updatedAt: Date.now(),
      }
      set(next)
      emit()
      return entries.get(keyFor(next.envelope))
    },
    remove: (key, ownerId) => {
      const resolved = resolveKey(key, ownerId)
      if (resolved !== undefined && erase(resolved)) emit()
    },
    subscribe: listener => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    hydrate: restored => {
      // Validate the complete snapshot before publishing any of it. A changed
      // command under an existing operation ID must not partially restore work.
      const updates = new Map<string, OfflineLedgerEntry>()
      restored.forEach(entry => {
        if (!isOwnedOfflineEnvelope(entry.envelope)) return
        const key = keyFor(entry.envelope)
        const current = updates.get(key) ?? entries.get(key)
        if (current && !isSameLedgerIntent(current, entry))
          throw new OfflineWriteIntentConflictError()
        // A stale storage snapshot cannot rearm an acknowledged operation.
        if (current?.status === 'succeeded') return
        if (current?.status === 'conflicted' && entry.status !== 'succeeded')
          return
        if (
          !current ||
          entry.status === 'succeeded' ||
          entry.updatedAt > current.updatedAt
        )
          updates.set(key, {
            ...entry,
            ...(entry.status === 'succeeded'
              ? { result: undefined, completionOnly: true as const }
              : {}),
            envelope:
              current?.envelope ?? freezeOfflineEnvelope(entry.envelope),
          })
      })
      updates.forEach(set)
      emit()
    },
    prune: (now = Date.now()) => {
      let changed = false
      entries.forEach((entry, key) => {
        if (
          entry.status === 'succeeded' &&
          now - entry.envelope.enqueuedAt > OFFLINE_QUEUE_MAX_AGE_MS &&
          now - entry.updatedAt > OFFLINE_LEDGER_RETENTION_MS
        ) {
          erase(key)
          changed = true
        } else if (
          entry.status === 'succeeded' &&
          !entry.completionOnly &&
          now - entry.updatedAt > OFFLINE_LEDGER_RETENTION_MS
        ) {
          set({ ...entry, ...completionMarker(entry) })
          changed = true
        }
      })
      if (changed) emit()
    },
  }
}

/**
 * Builds the envelope for one write and records it as `queued` in the same
 * breath.
 *
 * The ledger row has to exist *before* `mutationFn` runs, because when the
 * device is offline it never runs at all — TanStack parks the mutation and the
 * call site returns immediately. Without this the pending-writes UI would read
 * zero for exactly the situation it exists to describe.
 *
 * Call it at the call site, not inside `mutationFn`: `mutationFn` re-executes
 * on every replay, and an idempotency key regenerated per attempt is not an
 * idempotency key.
 */
export function enqueueOfflineWrite<TPayload>(
  ledger: OfflineLedger,
  input: {
    kind: OfflineMutationKind
    ownerId: string
    sessionGeneration?: number
    organizationId: string
    label: string
    payload: TPayload
    idempotencyKey: string
    recordRevision?: number
    now?: number
  },
): OfflineMutationEnvelope<TPayload> {
  if (!input.ownerId) throw new OfflineWriteOwnerError()
  const envelope: OfflineMutationEnvelope<TPayload> = {
    version: 2,
    ownerId: input.ownerId,
    sessionGeneration: input.sessionGeneration ?? 0,
    idempotencyKey: input.idempotencyKey,
    kind: input.kind,
    organizationId: input.organizationId,
    enqueuedAt: input.now ?? Date.now(),
    label: input.label,
    payload: input.payload,
    ...(input.recordRevision !== undefined
      ? { recordRevision: input.recordRevision }
      : {}),
  }

  const existing = ledger.get(input.idempotencyKey, input.ownerId)
  if (existing) {
    if (existing.status === 'succeeded' && existing.completionOnly) {
      if (!isSameOfflineIdentity(existing.envelope, envelope))
        throw new OfflineWriteIntentConflictError()
      // The earlier ID is closed. Never return a bodyless envelope as a draft,
      // or imply that a caller's edited body was the change that was saved.
      throw new OfflineWriteCompletedError()
    }
    if (!isSameOfflineWriteIntent(existing.envelope, envelope))
      throw new OfflineWriteIntentConflictError()
    // Capturing again is not consent to reset a failed or uncertain command.
    return existing.envelope as OfflineMutationEnvelope<TPayload>
  }

  if (
    (input.recordRevision !== undefined &&
      !isOfflineRecordRevision(input.recordRevision)) ||
    (input.kind === 'task.status' &&
      !isOfflineRecordRevision(input.recordRevision))
  )
    throw new OfflineWriteRevisionError()

  ledger.upsert({
    envelope,
    status: 'queued',
    attempts: 0,
    updatedAt: envelope.enqueuedAt,
  })

  return ledger.get(envelope.idempotencyKey, envelope.ownerId)!
    .envelope as OfflineMutationEnvelope<TPayload>
}

/** The ledger retains transport values, never a caller's editable draft object. */
function freezeOfflineEnvelope(
  envelope: OfflineMutationEnvelope,
): OfflineMutationEnvelope {
  const serialized = JSON.stringify(envelope.payload)
  const payload: unknown =
    serialized === undefined ? undefined : JSON.parse(serialized)
  const freeze = (value: unknown): void => {
    if (!value || typeof value !== 'object') return
    for (const child of Object.values(value)) freeze(child)
    Object.freeze(value)
  }
  freeze(payload)
  return Object.freeze({ ...envelope, payload })
}

/**
 * Ordering. `resumePausedMutations` fires every paused mutation at once, which
 * would let a status change land before the create that produced the task.
 * Routing every replay through one promise chain restores FIFO: writes execute
 * in the order the user made them, one at a time, and a failure does not stall
 * the ones behind it.
 */
export function createSerialGate(): <T>(fn: () => Promise<T>) => Promise<T> {
  let tail: Promise<unknown> = Promise.resolve()

  return function run<T>(fn: () => Promise<T>): Promise<T> {
    const result = tail.then(fn, fn)
    tail = result.then(
      () => undefined,
      () => undefined,
    )
    return result
  }
}

/** What to do with a write whose replay just failed. */
export type ReplayDisposition = 'retry' | 'drop' | 'fail'

/**
 * Conflict handling. A replayed write is an ordinary request against whatever
 * the server looks like *now* — the server stays authoritative, always.
 *
 *   no status  No confirmed answer. Only replay-safe writes may retry;
 *              at-most-once writes need an explicit decision.
 *   404 / 410  The target moved on without us: the task was deleted while the
 *              device was offline. There is nothing to apply, so drop it and
 *              say so rather than resurrecting a deleted record.
 *   409 / 412  Someone else changed it first. Surfaced, not retried — the user
 *              decides whether their offline intent still holds.
 *   4xx        Validation or permission. Retrying cannot help; surface the
 *              server's own message.
 *   5xx        Potentially transient, but not proof of rollback. The runner
 *              checks the write's replay contract before allowing a retry.
 */
export function classifyReplayError(
  status: number | undefined,
): ReplayDisposition {
  if (status === undefined) return 'retry'
  if (status === 404 || status === 410) return 'drop'
  if (status >= 400 && status < 500) return 'fail'
  return 'retry'
}

export class OfflineWriteExpiredError extends Error {
  constructor(label: string) {
    super(`"${label}" was queued too long ago to send safely.`)
    this.name = 'OfflineWriteExpiredError'
  }
}

export class OfflineWriteNeedsReviewError extends Error {
  constructor(label: string) {
    super(`"${label}" may already have been saved. Check before resending.`)
    this.name = 'OfflineWriteNeedsReviewError'
  }
}

/** A durable acknowledgement is not a reusable authorized server response. */
export class OfflineWriteCompletedError extends Error {
  constructor() {
    super(
      'That earlier change is already complete. Refresh before submitting another change.',
    )
    this.name = 'OfflineWriteCompletedError'
  }
}

export class OfflineWriteCompletionUnconfirmedError extends Error {
  constructor() {
    super(
      'The server saved this change, but its local confirmation could not be stored. Check the result before trying again.',
    )
    this.name = 'OfflineWriteCompletionUnconfirmedError'
  }
}

export class OfflineWriteIntentConflictError extends Error {
  readonly status = 409
  constructor() {
    super('This operation already belongs to a different saved change.')
    this.name = 'OfflineWriteIntentConflictError'
  }
}

/** Compare transport values, preserving array order and ignoring object key order. */
export function isSameOfflineWriteIntent(
  original: OfflineMutationEnvelope,
  candidate: OfflineMutationEnvelope,
): boolean {
  if (original === candidate) return true
  if (!isSameOfflineIdentity(original, candidate)) return false
  if (original.recordRevision !== candidate.recordRevision) return false
  const wire = (payload: unknown) => {
    const serialized = JSON.stringify(payload)
    if (serialized === undefined) return undefined
    return JSON.stringify(JSON.parse(serialized), (_key, value: unknown) =>
      value && typeof value === 'object' && !Array.isArray(value)
        ? Object.fromEntries(
            Object.entries(value).sort(([a], [b]) =>
              a < b ? -1 : a > b ? 1 : 0,
            ),
          )
        : value,
    )
  }
  try {
    return wire(original.payload) === wire(candidate.payload)
  } catch {
    return false
  }
}

function isSameOfflineIdentity(
  original: OfflineMutationEnvelope,
  candidate: OfflineMutationEnvelope,
): boolean {
  return (
    original.ownerId === candidate.ownerId &&
    original.organizationId === candidate.organizationId &&
    original.kind === candidate.kind &&
    original.idempotencyKey === candidate.idempotencyKey
  )
}

function isSameLedgerIntent(
  original: OfflineLedgerEntry,
  candidate: OfflineLedgerEntry,
): boolean {
  // A closed ID cannot be reused even with a different body. Its marker is
  // only a delivery barrier, never evidence that the new body was saved.
  if (
    (original.status === 'succeeded' && original.completionOnly) ||
    (candidate.status === 'succeeded' && candidate.completionOnly)
  )
    return isSameOfflineIdentity(original.envelope, candidate.envelope)
  return isSameOfflineWriteIntent(original.envelope, candidate.envelope)
}

function completionMarker(entry: OfflineLedgerEntry) {
  const envelope = entry.envelope
  return {
    completionOnly: true as const,
    result: undefined,
    message: undefined,
    envelope: Object.freeze({
      version: 2 as const,
      ownerId: envelope.ownerId,
      sessionGeneration: 0,
      organizationId: envelope.organizationId,
      idempotencyKey: envelope.idempotencyKey,
      kind: envelope.kind,
      enqueuedAt: envelope.enqueuedAt,
      label: 'Completed change',
      payload: undefined,
    }),
  }
}

/** These local decisions cannot be repaired by an automatic network retry. */
export function isTerminalOfflineWriteError(error: unknown): boolean {
  return (
    error instanceof SessionChangedError ||
    error instanceof OfflineWriteOwnerError ||
    error instanceof OfflineWriteKindError ||
    error instanceof OfflineWriteIntentConflictError ||
    error instanceof OfflineWriteNeedsReviewError ||
    error instanceof OfflineWriteCompletedError ||
    error instanceof OfflineWriteCompletionUnconfirmedError ||
    error instanceof OfflineWriteExpiredError ||
    error instanceof OfflineWriteDroppedError ||
    error instanceof OfflineWriteRevisionError
  )
}

export class OfflineWriteDroppedError extends Error {
  constructor(label: string) {
    super(`"${label}" could not be applied — the item no longer exists.`)
    this.name = 'OfflineWriteDroppedError'
  }
}

export interface OfflineRunnerContext {
  ledger: OfflineLedger
  gate: <T>(fn: () => Promise<T>) => Promise<T>
  /** Pulls an HTTP status off whatever error shape the host client throws. */
  getStatus: (error: unknown) => number | undefined
  now?: () => number
  getOwnerId: () => string | null
  captureSession?: () => {
    assertCurrent(): void
    confirmCurrent?(): Promise<void>
  }
  flush?: (ownerId?: string) => Promise<void>
}

export class OfflineWriteKindError extends Error {
  readonly status = 409
  constructor() {
    super('This saved change does not match its original action.')
    this.name = 'OfflineWriteKindError'
  }
}

/** Refuse foreign routes/workspaces before writing a dispatch marker, including autonomous replay. */
export function createScopedOfflineMutationFn<TPayload, TResult>(
  kind: OfflineMutationKind,
  run: (envelope: OfflineMutationEnvelope<TPayload>) => Promise<TResult>,
  context: OfflineRunnerContext,
  selection: { organizationId(): string | null; assertCurrent?(): void },
): (envelope: OfflineMutationEnvelope<TPayload>) => Promise<TResult> {
  const execute = createOfflineMutationFn(kind, run, context)
  return envelope => {
    assertOfflineWriteOwner(envelope, context.getOwnerId())
    if (envelope.kind !== kind) throw new OfflineWriteKindError()
    if (envelope.organizationId !== selection.organizationId())
      throw new SessionChangedError()
    selection.assertCurrent?.()
    return execute(envelope)
  }
}

/**
 * Wraps a plain API call into the `mutationFn` registered via
 * `setMutationDefaults`. Everything a rehydrated mutation needs to know about
 * itself travels in its envelope, so a mutation restored from disk into a
 * fresh process is fully self-describing.
 */
export function createOfflineMutationFn<TPayload, TResult>(
  kind: OfflineMutationKind,
  run: (envelope: OfflineMutationEnvelope<TPayload>) => Promise<TResult>,
  context: OfflineRunnerContext,
): (envelope: OfflineMutationEnvelope<TPayload>) => Promise<TResult> {
  const { ledger, gate, getStatus } = context
  const now = context.now ?? (() => Date.now())

  return captured =>
    gate(async () => {
      assertOfflineWriteOwner(captured, context.getOwnerId())
      if (captured.kind !== kind) throw new OfflineWriteKindError()
      const session = context.captureSession?.()
      const existing = ledger.get(captured.idempotencyKey, captured.ownerId)
      if (existing?.status === 'succeeded' && existing.completionOnly) {
        if (!isSameOfflineIdentity(existing.envelope, captured))
          throw new OfflineWriteIntentConflictError()
        throw new OfflineWriteCompletedError()
      }
      if (existing && !isSameOfflineWriteIntent(existing.envelope, captured))
        throw new OfflineWriteIntentConflictError()
      const envelope = (existing?.envelope ??
        freezeOfflineEnvelope(captured)) as OfflineMutationEnvelope<TPayload>
      const { idempotencyKey, label } = envelope

      // A restored completion blocks delivery but cannot supply a fresh result.
      if (existing?.status === 'succeeded') {
        if (now() - existing.updatedAt > OFFLINE_LEDGER_RETENTION_MS) {
          ledger.upsert({ ...existing, ...completionMarker(existing) })
          throw new OfflineWriteCompletedError()
        }
        await confirmCompletion()
        return existing.result as TResult
      }

      if (now() - envelope.enqueuedAt > OFFLINE_QUEUE_MAX_AGE_MS) {
        writeEntry(ledger, envelope, existing, {
          status: 'expired',
          updatedAt: now(),
          message: 'Queued too long ago to send safely.',
        })
        throw new OfflineWriteExpiredError(label)
      }

      // A write the user has already been asked about stays parked until they
      // explicitly reset its recovery state.
      if (existing?.status === 'expired')
        throw new OfflineWriteExpiredError(label)
      if (existing?.status === 'conflicted')
        throw existing.message === OFFLINE_TASK_WRITE_COMMITTED_MESSAGE
          ? new OfflineTaskWriteCommittedError()
          : new OfflineWriteRevisionError()

      if (
        existing?.status === 'needs-review' ||
        existing?.status === 'failed'
      ) {
        throw new OfflineWriteNeedsReviewError(label)
      }

      // `inflight` means an earlier attempt reached the network and we never
      // learned the outcome. Re-sending is only safe if a duplicate is
      // harmless; otherwise this is exactly the case worth asking about.
      if (
        (existing?.status === 'inflight' ||
          (existing?.status === 'queued' && existing.attempts > 0)) &&
        REPLAY_SAFETY[kind] === 'at-most-once'
      ) {
        writeEntry(ledger, envelope, existing, {
          status: 'needs-review',
          updatedAt: now(),
          message:
            'This may already have been saved before the connection dropped.',
        })
        throw new OfflineWriteNeedsReviewError(label)
      }

      // Historical status commands cannot invent a view-time revision now.
      // Keep the original command for recovery, but never dispatch it blind.
      if (
        kind === 'task.status' &&
        !isOfflineRecordRevision(envelope.recordRevision)
      ) {
        const error = new OfflineWriteRevisionError()
        writeEntry(ledger, envelope, existing, {
          status: 'failed',
          updatedAt: now(),
          message: error.message,
        })
        throw error
      }

      const attempts = (existing?.attempts ?? 0) + 1
      writeEntry(ledger, envelope, existing, {
        status: 'inflight',
        attempts,
        updatedAt: now(),
      })
      const dispatchEntry = ledger.get(idempotencyKey, envelope.ownerId)

      let dispatched = false
      let result: TResult
      try {
        // Persist the dispatch marker before I/O, so a terminated process can
        // distinguish a never-sent command from one with an unknown outcome.
        await context.flush?.(envelope.ownerId)
        await session?.confirmCurrent?.()
        session?.assertCurrent()
        assertOfflineWriteOwner(envelope, context.getOwnerId())
        if (ledger.get(idempotencyKey, envelope.ownerId) !== dispatchEntry)
          throw new OfflineWriteIntentConflictError()
        dispatched = true
        result = await run(envelope)
      } catch (error) {
        if (!dispatched) {
          if (ledger.get(idempotencyKey, envelope.ownerId) === dispatchEntry)
            ledger.patch(
              idempotencyKey,
              { status: 'queued', attempts: existing?.attempts ?? 0 },
              envelope.ownerId,
            )
          throw error
        }
        const status = getStatus(error)
        const disposition = classifyReplayError(status)
        if (kind === 'task.status' && status === 409) {
          const committed = isCommittedTaskStatusNotice(error, envelope)
          const conflict = committed
            ? new OfflineTaskWriteCommittedError()
            : new OfflineWriteRevisionError()
          writeEntry(ledger, envelope, existing, {
            status: 'conflicted',
            attempts,
            updatedAt: now(),
            message: conflict.message,
          })
          if (committed) {
            try {
              await context.flush?.(envelope.ownerId)
            } catch {
              throw new OfflineWriteCompletionUnconfirmedError()
            }
          }
          throw conflict
        }
        // A proxy timeout/5xx can follow a committed write. Without a receipt,
        // an HTTP answer is not proof that it is safe to create another record.
        if (
          REPLAY_SAFETY[kind] === 'at-most-once' &&
          (disposition === 'retry' || status === 408)
        ) {
          writeEntry(ledger, envelope, existing, {
            status: 'needs-review',
            attempts,
            updatedAt: now(),
            message: 'Delivery was not confirmed. Check before sending again.',
          })
          throw new OfflineWriteNeedsReviewError(label)
        }

        if (disposition === 'drop') {
          writeEntry(ledger, envelope, existing, {
            status: 'failed',
            attempts,
            updatedAt: now(),
            message: 'The item no longer exists.',
          })
          throw new OfflineWriteDroppedError(label)
        }

        if (disposition === 'fail' || attempts >= OFFLINE_QUEUE_MAX_ATTEMPTS) {
          writeEntry(ledger, envelope, existing, {
            status: 'failed',
            attempts,
            updatedAt: now(),
            message: toMessage(error),
          })
          throw error
        }

        // Only writes with a replay contract may be automatically tried again.
        ledger.patch(
          idempotencyKey,
          { status: 'queued', attempts },
          envelope.ownerId,
        )
        throw error
      }

      // Storage failure after acknowledgement is not a failed network write.
      // Keep the completed row and never feed it into retry/review transitions.
      writeEntry(ledger, envelope, existing, {
        status: 'succeeded',
        attempts,
        updatedAt: now(),
        result,
      })
      await confirmCompletion()
      return result

      async function confirmCompletion(): Promise<void> {
        try {
          await context.flush?.(envelope.ownerId)
        } catch {
          throw new OfflineWriteCompletionUnconfirmedError()
        }
        session?.assertCurrent()
        assertOfflineWriteOwner(envelope, context.getOwnerId())
      }
    })
}

function writeEntry(
  ledger: OfflineLedger,
  envelope: OfflineMutationEnvelope<unknown>,
  existing: OfflineLedgerEntry | undefined,
  changes: Partial<Omit<OfflineLedgerEntry, 'envelope'>>,
): void {
  ledger.upsert({
    envelope,
    status: existing?.status ?? 'queued',
    attempts: existing?.attempts ?? 0,
    updatedAt: existing?.updatedAt ?? envelope.enqueuedAt,
    result: existing?.result,
    message: existing?.message,
    ...changes,
  })
}

function toMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message
  return 'The change could not be saved.'
}

/** True for the mutation keys this module owns. */
export function isOfflineMutationKey(key: unknown): key is readonly string[] {
  return (
    Array.isArray(key) &&
    Object.values(OFFLINE_MUTATION_KEYS).some(
      allowed =>
        allowed.length === key.length &&
        allowed.every((part, index) => part === key[index]),
    )
  )
}

/**
 * The `shouldDehydrateMutation` predicate for both persisters.
 *
 * Two conditions, both required. `isPaused` keeps settled and in-progress
 * mutations out of storage. The key check is the allowlist, and it is the
 * reason a payment or an invoice send can never reach the queue no matter what
 * else changes about how those are written.
 */
export function shouldDehydrateOfflineMutation(mutation: {
  state: { isPaused: boolean }
  options: { mutationKey?: readonly unknown[] }
}): boolean {
  return (
    mutation.state.isPaused === true &&
    isOfflineMutationKey(mutation.options.mutationKey)
  )
}

/** Rows worth showing the user: queued work plus anything awaiting a decision. */
export function selectPendingWrites(
  entries: OfflineLedgerEntry[],
): OfflineLedgerEntry[] {
  return entries
    .filter(entry => entry.status !== 'succeeded')
    .sort((a, b) => a.envelope.enqueuedAt - b.envelope.enqueuedAt)
}

/** Rows the user has to resolve by hand — nothing retries these on its own. */
export function selectBlockedWrites(
  entries: OfflineLedgerEntry[],
): OfflineLedgerEntry[] {
  return selectPendingWrites(entries).filter(
    entry =>
      entry.status === 'failed' ||
      entry.status === 'conflicted' ||
      entry.status === 'needs-review' ||
      entry.status === 'expired' ||
      isUnconfirmedLegacyWrite(entry),
  )
}

/** Older clients requeued uncertain creates; never treat those as unsent work. */
export function isUnconfirmedLegacyWrite(entry: OfflineLedgerEntry): boolean {
  // Timestamp-bearing status updates need the same review as creates.
  return (
    entry.status === 'queued' &&
    entry.attempts > 0 &&
    REPLAY_SAFETY[entry.envelope.kind] === 'at-most-once'
  )
}

/** Missing/older envelopes are quarantined, never assigned to the next login. */
export function isOwnedOfflineEnvelope(
  value: unknown,
): value is OfflineMutationEnvelope {
  if (!value || typeof value !== 'object') return false
  const entry = value as Partial<OfflineMutationEnvelope>
  return (
    entry.version === 2 &&
    typeof entry.ownerId === 'string' &&
    !!entry.ownerId &&
    typeof entry.sessionGeneration === 'number' &&
    Number.isSafeInteger(entry.sessionGeneration) &&
    entry.sessionGeneration >= 0 &&
    typeof entry.organizationId === 'string' &&
    !!entry.organizationId &&
    typeof entry.idempotencyKey === 'string' &&
    !!entry.idempotencyKey &&
    typeof entry.enqueuedAt === 'number' &&
    Number.isFinite(entry.enqueuedAt) &&
    (entry.recordRevision === undefined ||
      isOfflineRecordRevision(entry.recordRevision)) &&
    typeof entry.kind === 'string' &&
    Object.prototype.hasOwnProperty.call(OFFLINE_MUTATION_KEYS, entry.kind)
  )
}

export class OfflineWriteOwnerError extends Error {
  readonly status = 409
  constructor() {
    super(
      'This change belongs to another session. Sign in to its original account to send it.',
    )
    this.name = 'OfflineWriteOwnerError'
  }
}

export function isOfflineRecordRevision(value: unknown): value is number {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= 0 &&
    value <= 2147483647
  )
}

export class OfflineWriteRevisionError extends Error {
  readonly status = 409
  constructor(
    message = 'Reload this task and make the change again. Its saved version cannot be checked safely.',
  ) {
    super(message)
    this.name = 'OfflineWriteRevisionError'
  }
}

export const OFFLINE_TASK_WRITE_COMMITTED_MESSAGE =
  'The task change was saved, but its current view is unavailable. Reload to check it before making another change. This queued change will not be sent again.'

/** A committed command notice does not supply a reusable reader view. */
export class OfflineTaskWriteCommittedError extends OfflineWriteRevisionError {
  constructor() {
    super(OFFLINE_TASK_WRITE_COMMITTED_MESSAGE)
    this.name = 'OfflineTaskWriteCommittedError'
  }
}

function isCommittedTaskStatusNotice(
  error: unknown,
  envelope: OfflineMutationEnvelope,
): boolean {
  if (!error || typeof error !== 'object') return false
  const source = error as { data?: unknown; response?: { data?: unknown } }
  const data = source.data ?? source.response?.data
  if (!data || typeof data !== 'object') return false
  const receipt = data as Record<string, unknown>
  const payload = envelope.payload as { taskId?: unknown } | undefined
  return (
    receipt.code === 'TASK_WRITE_COMMITTED' &&
    receipt.writeCommitted === true &&
    typeof payload?.taskId === 'string' &&
    receipt.taskId === payload.taskId &&
    isOfflineRecordRevision(envelope.recordRevision) &&
    isOfflineRecordRevision(receipt.revision) &&
    receipt.revision === envelope.recordRevision + 1
  )
}

export function assertOfflineWriteOwner(
  value: unknown,
  ownerId: string | null,
): asserts value is OfflineMutationEnvelope {
  if (!isOwnedOfflineEnvelope(value) || !ownerId || value.ownerId !== ownerId) {
    throw new OfflineWriteOwnerError()
  }
}

export function selectOwnerWrites(
  entries: OfflineLedgerEntry[],
  ownerId: string | null,
): OfflineLedgerEntry[] {
  return ownerId
    ? entries.filter(
        entry =>
          isOwnedOfflineEnvelope(entry.envelope) &&
          entry.envelope.ownerId === ownerId,
      )
    : []
}
